import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { Lock, Atom, Mail } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

// Safe storage for Safari private mode
const safeStorage = {
  removeItem: (key) => { try { localStorage.removeItem(key); } catch(e) {} }
};

const AdminLogin = () => {
  const { login, admin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Clear any existing sessions when visiting login page
  useEffect(() => {
    safeStorage.removeItem('adminToken');
  }, []);

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (admin) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-deep-blue grid-bg flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-12 h-12 rounded-xl bg-electric-cyan/20 flex items-center justify-center">
              <Atom className="w-7 h-7 text-electric-cyan" />
            </div>
          </div>
          <h1 className="font-outfit text-2xl font-bold text-white">
            Physics<span className="text-electric-cyan">Co</span> Admin
          </h1>
        </div>

        {/* Login Card */}
        <div className="glass-card p-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-full bg-electric-cyan/20 flex items-center justify-center">
              <Lock className="w-5 h-5 text-electric-cyan" />
            </div>
            <h2 className="font-outfit text-xl font-semibold text-white">Admin Login</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="input-dark pl-10"
                  data-testid="admin-email"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-slate-400 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-dark pl-10"
                  data-testid="admin-password"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center"
              data-testid="admin-login-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-deep-blue"></div>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
